import { useContext, useState, type ReactNode } from 'react';
import { isEqual, pick } from 'lodash-es';
import { useHistory } from 'react-router-dom';

import { Button, Heading, Paragraph, Modal } from '@hungryroot/ramen';
import { ROUTE_QUIZ_GOALS } from '@hungryroot/routes';
import {
  ROUTE_READY_MADE,
  ROUTE_EATING_HABITS_DIETARY_NEEDS,
  ROUTE_MACRO_NUTRIENT,
  ROUTE_POLARIZING,
  ROUTE_FOOD_QUALITIES,
} from '../../defs/routes';
import {
  DIETARY_QUESTIONS_PROPS,
  FEEDS_QUESTION_PROPS,
  HABITS_QUESTIONS_PROPS,
} from '../../questionProps';
import { QuizContext } from '../../quiz-context';
import { type Quiz } from '../../quiz-hooks';
import { Footer as QuizFooter } from '../Footer/Footer';
import { QuizForm } from '../QuizForm/QuizForm';
import { QuizToastContainer } from '../QuizToastContainer/QuizToastContainer';
import styles from './QuizQuestionScreen.module.css';

export interface Props {
  heroText?: ReactNode;
  subCopy?: string;
  children?: ReactNode;
  sectionLabel?: ReactNode;
  testid?: string;
  className?: string;
  secondaryCyid?: string;
  questionSectionClassName?: string;
  submitButtonDisabled?: boolean;
  onSubmitAnalytics?: () => void;
}

function getShouldSkip(loc: string, quiz: Quiz) {
  switch (loc) {
    case ROUTE_EATING_HABITS_DIETARY_NEEDS:
      return quiz.dietary === null || !quiz.dietary.length;
    case ROUTE_MACRO_NUTRIENT:
      return (
        quiz.macroNutrient === undefined ||
        quiz.macroNutrient === null ||
        !quiz.macroNutrient.length
      );
    case ROUTE_POLARIZING:
      return quiz.dislike === null || !quiz.dislike.length;
    case ROUTE_READY_MADE:
      return (
        quiz.readyMade === undefined ||
        quiz.readyMade === null ||
        !quiz.readyMade.length
      );
    case ROUTE_QUIZ_GOALS:
      return false;
    case ROUTE_FOOD_QUALITIES:
      return (
        quiz.foodQualities === undefined ||
        quiz.foodQualities === null ||
        !quiz.foodQualities.length
      );
    default:
      return false;
  }
}

export function QuizQuestionScreen(props: Props) {
  const {
    questionSectionClassName,
    testid,
    secondaryCyid,
    className,
    children,
    subCopy,
    heroText,
    submitButtonDisabled,
    onSubmitAnalytics,
  } = props;
  const history = useHistory();
  const {
    location: { pathname },
  } = history;

  const context = useContext(QuizContext);
  const { navigationState, quizToast, quiz, onSubmit } = context;
  const [previousQuiz] = useState(quiz);

  const [isFoodSelectionModalOpen, setIsFoodSelectionModalOpen] =
    useState(false);

  const isSkip = getShouldSkip(pathname, quiz);

  const { prev } = navigationState;
  const { clearToasts } = quizToast || {};
  const submitButtonStyle = 'default';

  const handleConfirm = () => {
    setIsFoodSelectionModalOpen(false);
    onSubmit(quiz);
  };

  const getFoodSelectionsChangeCallout = () => {
    const feedsAnswersFromPreviousQuiz = pick(
      previousQuiz,
      FEEDS_QUESTION_PROPS
    );
    const feedsAnswersFromQuiz = pick(quiz, FEEDS_QUESTION_PROPS);

    const dietaryAnswersFromPreviousQuiz = pick(
      previousQuiz,
      DIETARY_QUESTIONS_PROPS
    );

    const dietaryAnswersFromQuiz = pick(quiz, DIETARY_QUESTIONS_PROPS);

    const habitsAnswersFromPreviousQuiz = pick(
      previousQuiz,
      HABITS_QUESTIONS_PROPS
    );

    const habitsAnswersFromQuiz = pick(quiz, HABITS_QUESTIONS_PROPS);

    if (!isEqual(feedsAnswersFromPreviousQuiz, feedsAnswersFromQuiz)) {
      return `Heads up! If you update who you're feeding, the items and recipes you've selected may change to accommodate your new household.`;
    } else if (
      !isEqual(dietaryAnswersFromPreviousQuiz, dietaryAnswersFromQuiz)
    ) {
      return `Heads up! If you update your dietary needs, we'll refresh your food options to match. Select new recipes and items.`;
    } else if (!isEqual(habitsAnswersFromPreviousQuiz, habitsAnswersFromQuiz)) {
      return `Heads up! If you update how you eat, we'll refresh your food options to match. Select new recipes and items.`;
    }
  };

  return (
    <>
      <QuizForm
        footer={
          <QuizFooter
            childrenBefore={<QuizToastContainer />}
            backButton={
              prev !== null && (
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    history.push(prev);
                  }}
                  style="default"
                  size="medium"
                  prominence="secondary"
                  textPrimary="Back"
                />
              )
            }
            submitButton={
              <Button
                buttonType="submit"
                testid="quiz-button-next"
                onClick={(e) => {
                  if (submitButtonDisabled === true) {
                    e.preventDefault();
                  }

                  clearToasts?.();
                }}
                style={submitButtonStyle}
                disabled={submitButtonDisabled}
                size="medium"
                prominence="primary"
                textPrimary={isSkip ? 'Skip' : 'Next'}
              />
            }
          ></QuizFooter>
        }
        onSubmit={() => {
          onSubmitAnalytics?.();
          onSubmit(quiz);
        }}
        questionSectionClassName={questionSectionClassName}
        childrenContainerClassName={styles['delay-answers']}
        secondaryCyid={secondaryCyid}
        className={className}
        hero={
          <Heading testid={`${testid}-question-section-hero`} variant="H1Alt">
            {heroText}
          </Heading>
        }
        subCopy={
          subCopy ? (
            <Paragraph
              testid={`${testid}-question-section-subcopy`}
              className={styles['question-section-subcopy']}
            >
              {subCopy}
            </Paragraph>
          ) : null
        }
      >
        <section data-testid={testid} className={styles.center}>
          {children}
        </section>
      </QuizForm>
      {isFoodSelectionModalOpen && (
        <Modal
          isOpen={isFoodSelectionModalOpen}
          onActionClick={handleConfirm}
          onDismiss={() => {
            setIsFoodSelectionModalOpen(false);
          }}
          size="small"
          confirmButtonText="Update"
          secondaryButtonText="Cancel"
          ariaLabel="Food selections may change"
          heading="Food selections may change"
          body={getFoodSelectionsChangeCallout()}
        />
      )}
    </>
  );
}
